import { Event, ServerEvent, TServerSimpleCalendarEventList } from 'types/calendar';
import { RRule } from 'rrule';
import dayjs from 'utils/dayjs';
import { DEFAULT_CALENDAR_COLOR } from 'constants/common';

const toFloating = (d: dayjs.Dayjs) =>
    new Date(Date.UTC(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second()));

interface RecurringItem {
    startAt: string;
    endAt: string;
    timezone?: string | null;
}

interface ExpandedOccurrence {
    start: string;
    end: string;
}

export function expandRecurringInEventTz(
    item: RecurringItem,
    ruleOpts: Partial<InstanceType<typeof RRule>['origOptions']>,
    rangeStart: Date,
    rangeEnd: Date,
    exdates: string[],
    dtstartOverride?: dayjs.Dayjs,
    tzOverride?: string,
): ExpandedOccurrence[] {
    const tz = tzOverride || item.timezone || 'UTC';
    const durationMs = dayjs(item.endAt).diff(dayjs(item.startAt));
    const dtstart = dtstartOverride || dayjs(item.startAt).tz(tz);
    const floatingDtstart = toFloating(dtstart);

    const opts: any = { ...ruleOpts, dtstart: floatingDtstart, tzid: null };
    if (opts.until) {
        opts.until = toFloating(dayjs.utc(opts.until).tz(tz));
    }

    const rule = new RRule(opts);

    const dates = rule.between(
        toFloating(dayjs(rangeStart).tz(tz).startOf('day')),
        toFloating(dayjs(rangeEnd).tz(tz).endOf('day')),
        true,
    );

    const exdateSet = new Set(exdates);

    return dates
        .map((d) => {
            const wallStr = dayjs.utc(d).format('YYYY-MM-DDTHH:mm:ss');
            const start = dayjs.tz(wallStr, tz);
            if (exdateSet.has(start.format('YYYY-MM-DD'))) return null;
            return {
                start: start.toISOString(),
                end: start.add(durationMs, 'ms').toISOString(),
            };
        })
        .filter(Boolean) as ExpandedOccurrence[];
}

type RefillTaskItem = {
    patientName: string;
    description: string;
    submittedAt?: string;
    submittedOn: string;
    lastUpdatedAt?: string;
    lastUpdatedOn?: string;
    isRecurring?: boolean;
    recurrenceId?: number | null;
    canModifyRecurrence?: boolean;
    hasOutstandingBalance?: boolean;
    clientId?: number;
    textVisitId?: number;
    textVisitUuid?: string;
    providerTreatmentSubmitted?: boolean;
    startAt?: string;
    specialty?: string;
};

export const transferGoogleEventToServerEvent = (data: Event): ServerEvent => {
    return {
        start: data.start.dateTime,
        end: data.end.dateTime,
        eventId: data.id,
        colorId: data.channelId,
        description: data.description,
        email: data.attendees?.map((item) => item.email),
        location: data.location,
        summary: data.summary,
        timeZone: data.start.timeZone,
        providerId: data.providerId ?? undefined,
        channelId: data.channelId,
        serviceId: data.serviceId,
        duration: data.duration,
        meetingLink: data.meetingLink,
        patientName: data.patientName,
        patientPhone: data.patientPhone,
        sendForm: data.sendForm,
    };
};

export const transferServerEventToGoogleEvent = (data: ServerEvent): Event => {
    return {
        id: data.eventId,
        summary: data.summary,
        start: {
            timeZone: data.timeZone,
            dateTime: data.start,
        },
        end: {
            timeZone: data.timeZone,
            dateTime: data.end,
        },
        attendees: data.email?.map((email) => {
            return {
                email,
                displayName: email,
            };
        }),
        providerId: data.providerId,
        channelId: data.channelId,
        serviceId: data.serviceId,
        duration: data.duration,
        meetingLink: data.meetingLink,
        sendForm: data.sendForm,
        patientName: data.patientName,
        patientPhone: data.patientPhone,
        location: data.location,
        colorId: data.colorId,
        description: data.description,
    };
};

export const transferServiceSimpleListToGoogleEvent = (
    data: TServerSimpleCalendarEventList,
    start: string,
    end: string,
    providerId?: number,
): Event[] => {
    const ret: Event[] = [];
    data?.appointmentList?.forEach((item) => {
        const event: Event = {
            id: `${item.id}`,
            summary: item.title,
            start: {
                timeZone: 'UTC',
                dateTime: item.startAt,
            },
            end: {
                timeZone: 'UTC',
                dateTime: item.endAt,
            },
            attendees: [],
            description: '',
            type: 'appointment',
            appointmentStatus: item.appointmentStatus || undefined,
            color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
            scheduledBy: item.scheduledBy,
            serviceSpecialty: item.serviceSpecialty,
            clientId: item.clientId ?? undefined,
            isSample: item.isSample,
            timezone: item.timezone,
            providerId: item.providerId,
        };
        ret.push(event);
    });
    data?.googleList?.forEach((item) => {
        if (!item.recurrenceRule) {
            const event: Event = {
                id: `${item.id}`,
                summary: item.title,
                start: {
                    timeZone: 'UTC',
                    dateTime: item.startAt,
                },
                end: {
                    timeZone: 'UTC',
                    dateTime: item.endAt,
                },
                attendees: [],
                description: '',
                type: 'google',
                appointmentStatus: item.appointmentStatus || undefined,
                color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                scheduledBy: item.scheduledBy,
                serviceSpecialty: item.serviceSpecialty,
                timezone: item.timezone,
                providerId: item.providerId,
            };
            ret.push(event);
        } else {
            try {
                const startDay = new Date(start);
                const endDay = new Date(end);

                let ruleString = item.recurrenceRule;
                let exdateString: string | undefined = '';
                let dtstartString: string | undefined = '';
                if (ruleString.includes('\n')) {
                    const ruleArr = ruleString.split('\n');
                    exdateString = ruleArr.find((line) => line.startsWith('EXDATE')) || '';
                    dtstartString = ruleArr.find((line) => line.startsWith('DTSTART')) || '';
                    for (let i = 0; i < ruleArr.length; i++) {
                        const ruleItem = ruleArr[i];
                        if (ruleItem.startsWith('RRULE:')) {
                            ruleString = ruleItem;
                            break;
                        }
                    }
                }
                if (!ruleString.startsWith('RRULE:')) {
                    return;
                }

                const parsed = RRule.fromString(ruleString);

                let dtstartOverride: dayjs.Dayjs | undefined;
                let tzOverride: string | undefined;
                if (dtstartString) {
                    try {
                        const dtstartStringArr = dtstartString.substring(dtstartString.indexOf('=') + 1).split(':');
                        if (dtstartStringArr && dtstartStringArr.length === 2) {
                            dtstartOverride = dayjs.tz(dtstartStringArr[1], dtstartStringArr[0]);
                            if (!item.timezone) {
                                tzOverride = dtstartStringArr[0];
                            }
                        }
                    } catch (e) {
                        console.error('Error parsing DTSTART:', e);
                    }
                }

                const exdates: string[] = [];
                if (exdateString) {
                    try {
                        const firstColon = exdateString.indexOf(':');
                        const header = firstColon !== -1 ? exdateString.substring(0, firstColon) : exdateString;
                        const valuesStr = firstColon !== -1 ? exdateString.substring(firstColon + 1) : '';
                        const tzMatch = header.match(/TZID=([^;:]+)/);
                        const exValues = valuesStr ? valuesStr.split(',') : [];
                        exValues.forEach((v) => {
                            try {
                                if (tzMatch) {
                                    exdates.push(dayjs.tz(v.trim(), tzMatch[1]).format('YYYY-MM-DD'));
                                } else {
                                    exdates.push(dayjs.utc(v.trim()).format('YYYY-MM-DD'));
                                }
                            } catch (e) { /* skip invalid */ }
                        });
                    } catch (e) {
                        console.error('Error parsing EXDATE:', e);
                    }
                }

                const occurrences = expandRecurringInEventTz(
                    item,
                    parsed.origOptions,
                    startDay,
                    endDay,
                    exdates,
                    dtstartOverride,
                    tzOverride,
                );

                occurrences.forEach((occ) => {
                    const event: Event = {
                        id: `${item.id}`,
                        summary: item.title,
                        start: {
                            timeZone: 'UTC',
                            dateTime: occ.start,
                        },
                        end: {
                            timeZone: 'UTC',
                            dateTime: occ.end,
                        },
                        attendees: [],
                        description: '',
                        type: 'google',
                        appointmentStatus: item.appointmentStatus || undefined,
                        color: item.calendarColor || DEFAULT_CALENDAR_COLOR,
                        scheduledBy: item.scheduledBy,
                        serviceSpecialty: item.serviceSpecialty,
                        timezone: item.timezone,
                        providerId: item.providerId,
                    };
                    ret.push(event);
                });
            } catch (e) {
                console.error('Error expanding recurring google event:', item.id, e);
            }
        }
    });

    data?.blockerList?.forEach((item) => {
        if (!item.recurrenceRule) {
            const event: Event = {
                id: `${item.id}`,
                summary: item.title,
                start: {
                    timeZone: 'UTC',
                    dateTime: item.startAt,
                },
                end: {
                    timeZone: 'UTC',
                    dateTime: item.endAt,
                },
                attendees: [],
                description: '',
                type: 'blocker',
                appointmentStatus: item.appointmentStatus || undefined,
                scheduledBy: item.scheduledBy,
                serviceSpecialty: item.serviceSpecialty,
                isSample: item.isSample,
                timezone: item.timezone,
                providerId: item.providerId,
            };
            ret.push(event);
        } else if (item.recurrenceRule) {
            try {
                const startDay = new Date(start);
                const endDay = new Date(end);

                let ruleString = item.recurrenceRule;
                if (ruleString.includes('\n')) {
                    const ruleArr = ruleString.split('\n');
                    for (let i = 0; i < ruleArr.length; i++) {
                        if (ruleArr[i].startsWith('RRULE:')) {
                            ruleString = ruleArr[i];
                            break;
                        }
                    }
                }
                if (!ruleString.startsWith('RRULE:')) {
                    return;
                }

                const parsed = RRule.fromString(ruleString);
                const occurrences = expandRecurringInEventTz(
                    item,
                    parsed.origOptions,
                    startDay,
                    endDay,
                    [],
                );

                occurrences.forEach((occ) => {
                    const event: Event = {
                        id: `${item.id}`,
                        summary: item.title,
                        start: {
                            timeZone: 'UTC',
                            dateTime: occ.start,
                        },
                        end: {
                            timeZone: 'UTC',
                            dateTime: occ.end,
                        },
                        attendees: [],
                        description: '',
                        type: 'blocker',
                        appointmentStatus: item.appointmentStatus || undefined,
                        scheduledBy: item.scheduledBy,
                        serviceSpecialty: item.serviceSpecialty,
                        timezone: item.timezone,
                        providerId: item.providerId,
                    };
                    ret.push(event);
                });
            } catch (e) {
                console.error('Error expanding recurring blocker:', item.id, e);
            }
        }
    });

    // Text visits are task-like items (shown as a pill such as "2 text visits").
    // Backend returns a flat list; for calendar UI we aggregate by date.
    const textVisits = data?.textVisitList || [];
    if (textVisits.length > 0) {
        const byDayTextVisits: Record<string, number> = {};
        const byDayTextVisitItems: Record<string, RefillTaskItem[]> = {};
        const byDayPrescriptionRefills: Record<string, number> = {};
        const byDayPrescriptionRefillItems: Record<string, RefillTaskItem[]> = {};

        const todayKey = dayjs().format('YYYY-MM-DD');

        for (const tv of textVisits) {
            // Use startAt as the primary date key so recurring occurrences appear on their
            // correct date (e.g. a recurrence with startAt=Apr 13 shows on Apr 13, not
            // the original patientSubmitAt date). Fall back to other timestamps if absent.
            const ts = tv.snoozeUntil || tv.startAt || tv.patientSubmitAt || tv.updatedAt || tv.assignedProviderAt;
            if (!ts) continue;
            // Backend stores UTC (with or without Z). Force UTC parse, then convert to local
            // for the day key so it lines up with Week/Month/Day cells (which use local format).
            const rawDayKey = dayjs.utc(ts).local().format('YYYY-MM-DD');
            // If the task date is in the past, pin it to today so it stays visible
            // in the current calendar view (unresolved past tasks surface to today).
            const dayKey = rawDayKey < todayKey ? todayKey : rawDayKey;

            if (tv.serviceType === 'PRESCRIPTION_REFILL') {
                byDayPrescriptionRefills[dayKey] = (byDayPrescriptionRefills[dayKey] || 0) + 1;

                const submittedAt = tv.patientSubmitAt || tv.assignedProviderAt || tv.updatedAt;
                const { updatedAt } = tv;
                const patientName = [tv.patientFirstName, tv.patientLastName].filter(Boolean).join(' ') || 'Unknown';
                const stateTag = tv.state ? `[${tv.state}]` : '';
                const specialty = tv.specialty || tv.serviceType || 'Prescription refill';
                const description = `${stateTag} ${specialty}: Prescription refill`.trim();

                const item: RefillTaskItem = {
                    patientName,
                    description,
                    submittedAt: submittedAt || undefined,
                    submittedOn: submittedAt ? dayjs(submittedAt).utc().format('MMM D') : '',
                    lastUpdatedAt: updatedAt || undefined,
                    lastUpdatedOn: updatedAt ? dayjs(updatedAt).utc().format('MMM D') : undefined,
                    isRecurring: !!(tv.isRecurring || tv.recurrenceId),
                    recurrenceId: tv.recurrenceId,
                    canModifyRecurrence: !!(tv as any).canModifyRecurrence,
                    hasOutstandingBalance: !!(tv.hasOutstandingBalance || tv.hasUnpaidOrFailedStatus),
                    clientId: tv.clientId,
                    textVisitId: tv.id,
                    textVisitUuid:
                        tv.textVisitUuid ||
                        // fallback for older payloads
                        (tv as any).uuid ||
                        (tv as any).caseUuid ||
                        (tv as any).text_visit_uuid,
                    providerTreatmentSubmitted: tv.providerTreatmentSubmitted,
                    startAt: tv.startAt ?? undefined,
                    specialty: tv.specialty,
                };

                if (!byDayPrescriptionRefillItems[dayKey]) byDayPrescriptionRefillItems[dayKey] = [];
                byDayPrescriptionRefillItems[dayKey].push(item);
            } else {
                byDayTextVisits[dayKey] = (byDayTextVisits[dayKey] || 0) + 1;

                const submittedAt = tv.patientSubmitAt || tv.assignedProviderAt || tv.updatedAt;
                const { updatedAt } = tv;
                const patientName = [tv.patientFirstName, tv.patientLastName].filter(Boolean).join(' ') || 'Unknown';
                const stateTag = tv.state ? `[${tv.state}]` : '';
                const specialty = tv.specialty || tv.serviceType || 'Text visit';
                const description = `${stateTag} ${specialty}`.trim();

                const tvItem: RefillTaskItem = {
                    patientName,
                    description,
                    submittedAt: submittedAt || undefined,
                    submittedOn: submittedAt ? dayjs(submittedAt).format('MMM D') : '',
                    lastUpdatedAt: updatedAt || undefined,
                    lastUpdatedOn: updatedAt ? dayjs(updatedAt).format('MMM D') : undefined,
                    isRecurring: !!(tv.isRecurring || tv.recurrenceId),
                    recurrenceId: tv.recurrenceId,
                    canModifyRecurrence: !!(tv as any).canModifyRecurrence,
                    hasOutstandingBalance: !!(tv.hasOutstandingBalance || tv.hasUnpaidOrFailedStatus),
                    clientId: tv.clientId,
                    textVisitId: tv.id,
                    textVisitUuid:
                        tv.textVisitUuid || (tv as any).uuid || (tv as any).caseUuid || (tv as any).text_visit_uuid,
                    providerTreatmentSubmitted: tv.providerTreatmentSubmitted,
                    startAt: tv.startAt ?? undefined,
                    specialty: tv.specialty,
                };

                if (!byDayTextVisitItems[dayKey]) byDayTextVisitItems[dayKey] = [];
                byDayTextVisitItems[dayKey].push(tvItem);
            }
        }

        const pushTaskPill = (
            dayKey: string,
            idPrefix: string,
            summary: string,
            extra?: {
                prescriptionRefillItems?: RefillTaskItem[];
                textVisitItems?: RefillTaskItem[];
            },
        ) => {
            // Use local midnight so the ISO string stays consistent with the local dayKey.
            const startAt = dayjs(`${dayKey}T00:00:00`);
            ret.push({
                id: `${idPrefix}_${dayKey}`,
                summary,
                description: '',
                start: { timeZone: 'UTC', dateTime: startAt.toISOString() },
                end: { timeZone: 'UTC', dateTime: startAt.add(10, 'minute').toISOString() },
                attendees: [],
                type: 'task',
                color: DEFAULT_CALENDAR_COLOR,
                // Keep a stable day key so calendar views can place pills correctly
                // regardless of browser/selected timezone conversions.
                taskDayKey: dayKey,
                ...extra,
            });
        };

        Object.entries(byDayPrescriptionRefills).forEach(([dayKey, count]) => {
            pushTaskPill(dayKey, 'prescription_refills', `📃 ${count} Prescription refills`, {
                prescriptionRefillItems: byDayPrescriptionRefillItems[dayKey] || [],
            });
        });
        Object.entries(byDayTextVisits).forEach(([dayKey, count]) => {
            pushTaskPill(dayKey, 'text_visits', `✏️ ${count} text visits`, {
                textVisitItems: byDayTextVisitItems[dayKey] || [],
            });
        });
    }
    if (providerId !== undefined) {
        ret.forEach((e) => {
            if (!e.providerId) {
                e.providerId = providerId;
            }
        });
    }
    return ret;
};

export const EventData = {
    transferGoogleEventToServerEvent,
    transferServerEventToGoogleEvent,
    transferServiceSimpleListToGoogleEvent,
};
